import { useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import { BodySmallRegular, View, XStack } from 'smoothie';

const LEGEND_TRANSITION_MS = 220;

export interface ChartLegendItem {
    color: string;
    label: string;
    value: string;
}

const ChartLegendItemRow = ({
    item,
    index,
    isSelected,
    isDimmed,
    onSelectIndex,
}: {
    item: ChartLegendItem;
    index: number;
    isSelected: boolean;
    isDimmed: boolean;
    onSelectIndex?: (index: number) => void;
}) => {
    const opacity = useRef(new Animated.Value(isDimmed ? 0.5 : 1)).current;

    useEffect(() => {
        Animated.timing(opacity, {
            toValue: isDimmed ? 0.5 : 1,
            duration: LEGEND_TRANSITION_MS,
            useNativeDriver: true,
        }).start();
    }, [isDimmed, opacity]);

    const label =
        item.value === '' ? item.label : `${item.label} · ${item.value}`;

    return (
        <Animated.View style={{ opacity }}>
            <XStack
                alignItems="center"
                gap="$1"
                maxWidth="100%"
                {...(onSelectIndex != null
                    ? {
                          cursor: 'pointer' as const,
                          pressStyle: { opacity: 0.7 },
                          onPress: () => {
                              onSelectIndex(index);
                          },
                          accessibilityRole: 'button' as const,
                          accessibilityState: { selected: isSelected },
                          accessibilityLabel: item.label,
                      }
                    : {})}
            >
                <View
                    width={16}
                    height={16}
                    borderRadius={4}
                    flexShrink={0}
                    style={{ backgroundColor: item.color }}
                />
                <BodySmallRegular color="$black">{label}</BodySmallRegular>
            </XStack>
        </Animated.View>
    );
};

export const ChartLegend = ({
    items,
    selectedIndex = null,
    onSelectIndex,
}: {
    items: ChartLegendItem[];
    selectedIndex?: number | null;
    onSelectIndex?: (index: number) => void;
}) => (
    <XStack width="100%" flexWrap="wrap" gap="$3" justifyContent="center">
        {items.map((item, index) => {
            const isSelected = selectedIndex === index;

            return (
                <ChartLegendItemRow
                    key={`${item.label}-${index.toString()}`}
                    item={item}
                    index={index}
                    isSelected={isSelected}
                    isDimmed={selectedIndex != null && !isSelected}
                    onSelectIndex={onSelectIndex}
                />
            );
        })}
    </XStack>
);
